/**
 * Reusable text/caption style presets.
 * Used by both the Text and Caption properties panels.
 * Add new presets here and they'll appear everywhere automatically.
 *
 * Each preset can contain:
 *   fontFamily, fontSize, color, bg (preview background),
 *   stroke, strokeThickness, lineJoin,
 *   dropShadow, dropShadowColor, dropShadowBlur, dropShadowDistance,
 *   highlightColor (enables word-by-word highlighting via PIXI.Container of word sprites),
 *   maxLines, backgroundColor, backgroundPaddingX,
 *   animation, category
 */
import gsap from 'gsap';

export const ANIMATION_FUNCTIONS = {
    fadeIn: (target, isPixi, originalX = 0, originalY = 0) => {
        const opacity = isPixi ? 'alpha' : 'opacity';
        gsap.fromTo(target, { [opacity]: 0 }, { [opacity]: 1, duration: 0.6 });
    },
    fadeInRight: (target, isPixi, originalX = 0, originalY = 0) => {
        const opacity = isPixi ? 'alpha' : 'opacity';
        const dist = isPixi ? 60 : 30;
        const startX = isPixi ? originalX + dist : dist;
        gsap.fromTo(target, { [opacity]: 0, x: startX }, { [opacity]: 1, x: isPixi ? originalX : 0, duration: 0.5, ease: 'power2.out' });
    },
    fadeInLeft: (target, isPixi, originalX = 0, originalY = 0) => {
        const opacity = isPixi ? 'alpha' : 'opacity';
        const dist = isPixi ? 60 : 30;
        const startX = isPixi ? originalX - dist : -dist;
        gsap.fromTo(target, { [opacity]: 0, x: startX }, { [opacity]: 1, x: isPixi ? originalX : 0, duration: 0.5, ease: 'power2.out' });
    },
    fadeInTop: (target, isPixi, originalX = 0, originalY = 0) => {
        const opacity = isPixi ? 'alpha' : 'opacity';
        const dist = isPixi ? 50 : 25;
        const startY = isPixi ? originalY - dist : -dist;
        gsap.fromTo(target, { [opacity]: 0, y: startY }, { [opacity]: 1, y: isPixi ? originalY : 0, duration: 0.5, ease: 'power2.out' });
    },
    fadeInBottom: (target, isPixi, originalX = 0, originalY = 0) => {
        const opacity = isPixi ? 'alpha' : 'opacity';
        const dist = isPixi ? 50 : 25;
        const startY = isPixi ? originalY + dist : dist;
        gsap.fromTo(target, { [opacity]: 0, y: startY }, { [opacity]: 1, y: isPixi ? originalY : 0, duration: 0.5, ease: 'power2.out' });
    },
    typewriter: (target, isPixi, originalX = 0, originalY = 0) => {
        const opacity = isPixi ? 'alpha' : 'opacity';
        const startX = isPixi ? originalX - 30 : -15;
        gsap.fromTo(target, { [opacity]: 0, x: startX }, {
            [opacity]: 1, x: isPixi ? originalX : 0, duration: 0.4, ease: 'steps(6)'
        });
    },
    scaleUp: (target, isPixi, originalX = 0, originalY = 0) => {
        if (isPixi) {
            gsap.fromTo(target.scale, { x: 0.3, y: 0.3 }, { x: 1, y: 1, duration: 0.5, ease: 'back.out(2.5)' });
            gsap.fromTo(target, { alpha: 0 }, { alpha: 1, duration: 0.25 });
        } else {
            gsap.fromTo(target, { scale: 0.3, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(2.5)' });
        }
    },
    slideUp: (target, isPixi, originalX = 0, originalY = 0) => {
        const opacity = isPixi ? 'alpha' : 'opacity';
        const dist = isPixi ? 40 : 20;
        const startY = isPixi ? originalY + dist : dist;
        gsap.fromTo(target, { [opacity]: 0, y: startY }, { [opacity]: 1, y: isPixi ? originalY : 0, duration: 0.45, ease: 'power3.out' });
    },
    pulse: (target, isPixi, originalX = 0, originalY = 0) => {
        if (isPixi) {
            gsap.fromTo(target.scale, { x: 0.8, y: 0.8 }, { x: 1, y: 1, duration: 0.4, ease: 'elastic.out(1.2, 0.4)' });
            gsap.fromTo(target, { alpha: 0.5 }, { alpha: 1, duration: 0.3 });
        } else {
            gsap.fromTo(target, { scale: 0.8, opacity: 0.5 }, { scale: 1, opacity: 1, duration: 0.4, ease: 'elastic.out(1.2, 0.4)' });
        }
    },
    bounce: (target, isPixi, originalX = 0, originalY = 0) => {
        const dist = isPixi ? 40 : 25;
        const startY = isPixi ? originalY - dist : -dist;
        const opacity = isPixi ? 'alpha' : 'opacity';
        gsap.fromTo(target, { y: startY, [opacity]: 0 }, { y: isPixi ? originalY : 0, [opacity]: 1, duration: 0.7, ease: 'bounce.out' });
    },
    popIn: (target, isPixi, originalX = 0, originalY = 0) => {
        if (isPixi) {
            gsap.fromTo(target.scale, { x: 1.6, y: 1.6 }, { x: 1, y: 1, duration: 0.45, ease: 'back.out(3)' });
            gsap.fromTo(target, { alpha: 0 }, { alpha: 1, duration: 0.2 });
        } else {
            gsap.fromTo(target, { scale: 1.6, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.45, ease: 'back.out(3)' });
        }
    },
    glitch: (target, isPixi, originalX = 0, originalY = 0) => {
        const opacity = isPixi ? 'alpha' : 'opacity';
        const baseX = isPixi ? originalX : 0;
        const tl = gsap.timeline();
        tl.set(target, { [opacity]: 1 });
        tl.to(target, { x: baseX + 8, duration: 0.04 });
        tl.to(target, { x: baseX - 6, duration: 0.04 });
        tl.to(target, { x: baseX + 4, duration: 0.04 });
        tl.to(target, { x: baseX - 3, duration: 0.04 });
        tl.to(target, { x: baseX, duration: 0.04 });
    },
    bounceIn: (target, isPixi, originalX = 0, originalY = 0) => {
        if (isPixi) {
            gsap.fromTo(target.scale, { x: 0.2, y: 0.2 }, { x: 1, y: 1, duration: 0.7, ease: 'bounce.out' });
            gsap.fromTo(target, { alpha: 0 }, { alpha: 1, duration: 0.2 });
        } else {
            gsap.fromTo(target, { scale: 0.2, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.7, ease: 'bounce.out' });
        }
    },
};

/**
 * Build an HTML string for word-by-word highlighted captions.
 * Used by canvasStore ticker and export controller.
 *
 * @param {Array} words        - Array of { text, start, end }
 * @param {number} currentTimeMs - Current playback time in ms
 * @param {string} baseColor   - Default word color (e.g. '#ffffff')
 * @param {string} highlightColor - Active word color (e.g. '#ffff00')
 * @param {object} [opts]      - Optional: { highlightBg }
 * @returns {string} HTML string with <span> wrapped words
 */
export function buildHighlightedHTML(words, currentTimeMs, baseColor, highlightColor, opts = {}) {
    if (!words || words.length === 0) return '';
    return words.map(w => {
        const isActive = currentTimeMs >= w.start && currentTimeMs <= w.end;
        const color = isActive ? highlightColor : baseColor;
        const bg = isActive && opts.highlightBg ? `background-color:${opts.highlightBg};border-radius:4px;padding:0 4px;` : '';
        return `<span style="color:${color};${bg}">${w.text}</span>`;
    }).join(' ');
}

export const DEFAULT_CAPTION_STYLE = {
    fontFamily: 'Arial',
    fontSize: 48,
    color: '#ffffff',
    animation: 'fadeIn',
    maxLines: 1,
    backgroundPaddingX: 16,
    dropShadow: false,
    dropShadowColor: '#000000',
    dropShadowBlur: 4,
    dropShadowDistance: 0,
};

// ─── PRESET CATEGORIES ─────────────────────────────────────────
// Presets are grouped by visual category for easy browsing.

export const TEXT_STYLE_PRESETS = [
    //Custom Animated Styles ────────────────────────────────────
    { key: 'custom_default_caption', label: 'Default', category: 'Custom', fontFamily: 'Arial', fontSize: 48, color: '#ffffff', bg: '#111111', animation: 'fadeIn', maxLines: 1 },
    // 1. White text, active word wrapped in blue box
    { key: 'custom_blue_box', label: 'Blue Box', category: 'Custom', fontFamily: 'Montserrat', fontSize: 52, color: '#ffffff', bg: '#111', stroke: '#000000', strokeThickness: 6, lineJoin: 'round', highlightColor: '#ffffff', highlightBg: '#2563eb', displayMode: 'karaoke', animation: 'fadeIn' },
    // 2. Classic terminal-style typewriter reveal
    { key: 'custom_typewriter', label: 'Typewriter', category: 'Custom', fontFamily: 'Courier New', fontSize: 48, color: '#ffffff', bg: '#0c0c0c', displayMode: 'progressive', wordAnimation: 'typewriter', animation: 'typewriter' },
    // 3. Two phrases, white + yellow halo, active word yellow
    { key: 'custom_yellow_halo', label: 'Yellow Halo', category: 'Custom', fontFamily: 'Arial', fontSize: 50, color: '#ffffff', bg: '#0d0d0d', highlightColor: '#fbbf24', displayMode: 'phraseHighlight', dropShadow: true, dropShadowColor: '#fbbf24', dropShadowBlur: 14, dropShadowDistance: 0, animation: 'fadeIn' },
    // 4. Pale yellow: words stay dim until spoken, then stay fully visible
    { key: 'custom_pale_reveal', label: 'Pale Reveal', category: 'Custom', fontFamily: 'Inter', fontSize: 48, color: '#ffdd33', bg: '#0a0a0a', displayMode: 'phraseHighlight', revealOpacityMode: 'activeTrail', inactiveOpacity: 0.4, animation: 'fadeIn' },
    // 5. Arial typewriter with block cursor and moving black text background
    { key: 'custom_green_bounce', label: 'Block Typewriter', category: 'Custom', fontFamily: 'Arial', fontSize: 52, color: '#ffffff', bg: '#0a0a0a', animation: 'typewriter', typewriterCursor: 'block', typewriterBackgroundColor: '#000000', typewriterBackgroundPaddingX: 10, typewriterBackgroundPaddingY: 6, typewriterBackgroundRadius: 4 },
    // 6. Only the currently spoken word is visible
    { key: 'custom_active_word_only', label: 'Active Word', category: 'Custom', fontFamily: 'Montserrat', fontSize: 54, color: '#ffffff', bg: '#101010', stroke: '#000000', strokeThickness: 6, lineJoin: 'round', displayMode: 'wordByWord', wordAnimation: 'popIn', animation: 'fadeIn' },
    // 7. Typewriter-like progressive words with active-word blur cover instead of a cursor
    // { key: 'custom_typewriter_blur', label: 'Type Blur', category: 'Custom', fontFamily: 'Arial', fontSize: 52, color: '#ffffff', bg: '#111111', displayMode: 'progressive', wordAnimation: null, activeWordBlur: true, activeWordBlurColor: '#d7dce8', activeWordBlurRadius: 52, activeWordBlurTrailMs: 520, activeWordBlurRevealMs: 190, activeWordBlurOpacity: 0.94, activeWordBlurOffsetX: 8, activeWordBlurOffsetY: 2, activeWordBlurWordAlpha: 0, activeWordBlurXRatio: 0.74, animation: 'fadeIn' },
];
